import { upperCase } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@config/axios/axios.config';

import { paths } from '@common/types';
import { stableKey } from '@common/cache/util.ts';

interface IBuildingListProps {
  page: number;
  limit: number;
  sort_by?: string;
  sort_order?: string;
  search: string;
  partner_id?: string;
  operator_id?: string;
  county?: string;
  state?: string;
}

const buildingsRequest = async (props: IBuildingListProps) => {
  const response = axiosInstance.get<
    paths[`/record/buildings`]['get']['responses']['200']['content']['application/json']
  >(`/record/buildings`, {
    params: {
      page: props.page,
      limit: props.limit,
      ...(props.search && { search: props.search }),
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.sort_by && { sort_by: props.sort_by }),
      ...(props.partner_id && { partner_id: props.partner_id }),
      ...(props.operator_id && { operator_id: props.operator_id }),
      ...(props.state && { state: upperCase(props.state) }),
      ...(props.county && { county: props.county }),
    },
  });
  const data = (await response).data;

  return data;
};

export const useBuildingListQuery = (props: IBuildingListProps) => {
  const query = useQuery({
    queryKey: ['buildings', stableKey(props)],
    queryFn: () => buildingsRequest(props),
    placeholderData: keepPreviousData,
  });

  return query;
};

interface IBuildingProps {
  building_id?: string;
}

const buildingRequest = async (props: IBuildingProps) => {
  const response = axiosInstance.get<
    paths[`/building/{record_id}`]['get']['responses']['200']['content']['application/json']
  >(`/building/${props.building_id}`);
  const data = (await response).data;

  return data;
};

export const useBuildingQuery = (props: IBuildingProps) => {
  const query = useQuery({
    queryKey: ['building', props.building_id],
    queryFn: () => buildingRequest(props),
    enabled: Boolean(props.building_id),
  });

  return query;
};

interface IBuildingHistoryProps {
  page: number;
  limit: number;
  building_id: string;
  enabled: boolean;
}

const buildingHistoryRequest = async (props: IBuildingHistoryProps) => {
  const response = axiosInstance.get<
    paths[`/building/{record_id}/history`]['get']['responses']['200']['content']['application/json']
  >(`/building/${props.building_id}/history`, {
    params: {
      page: props.page,
      limit: props.limit,
    },
  });
  const data = (await response).data;

  return data;
};

export const useBuildingHistoryQuery = (props: IBuildingHistoryProps) => {
  const query = useQuery({
    enabled: !!props.enabled,
    queryKey: ['building', props.building_id, 'history', props.page],
    queryFn: () => buildingHistoryRequest(props),
  });

  return query;
};

interface IBuildingsShortQueryProps {
  search?: string;
}

const buildingsShortRequest = async ({ search }: IBuildingsShortQueryProps) => {
  const response = axiosInstance.get<
    paths[`/record/buildings/short`]['get']['responses']['200']['content']['application/json']
  >(`/record/buildings/short`, {
    params: {
      ...(search && { search }),
    },
  });
  const data = (await response).data;
  return data;
};

export const useBuildingsShortQuery = (
  props: IBuildingsShortQueryProps & { disabled?: boolean },
) => {
  const orgQuery = useQuery({
    enabled: !props.disabled,
    queryKey: ['buildingsShort', props.search],
    queryFn: () => buildingsShortRequest(props),
  });

  return orgQuery;
};
